"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const loglevel_1 = __importDefault(require("loglevel"));
const ethereumjs_tx_1 = require("ethereumjs-tx");
const Constants_1 = require("../common/Constants");
const RelaySelectionManager_1 = __importDefault(require("./RelaySelectionManager"));
const GSNConfigurator_1 = require("./GSNConfigurator");
const Utils_1 = require("../common/Utils");
const events_1 = require("events");
const GsnEvents_1 = require("./GsnEvents");
// generate "approvalData" and "paymasterData" for a request.
// both are bytes arrays. paymasterData is part of the client request.
// approvalData is created after request is filled and signed.
exports.EmptyDataCallback = async () => {
    return '0x';
};
exports.GasPricePingFilter = (pingResponse, gsnTransactionDetails) => {
    if (gsnTransactionDetails.gasPrice != null &&
        parseInt(pingResponse.minGasPrice) > parseInt(gsnTransactionDetails.gasPrice)) {
        throw new Error(`Proposed gas price: ${gsnTransactionDetails.gasPrice}; relay's MinGasPrice: ${pingResponse.minGasPrice}`);
    }
};
class RelayClient {
    /**
     * create a RelayClient library object, to force contracts to go through a relay.
     */
    constructor(provider, configOverride, overrideDependencies) {
        this.emitter = new events_1.EventEmitter();
        this.initialized = false;
        const config = GSNConfigurator_1.configureGSN(configOverride);
        const dependencies = GSNConfigurator_1.getDependencies(config, provider, overrideDependencies);
        this.config = dependencies.config;
        this.httpClient = dependencies.httpClient;
        this.contractInteractor = dependencies.contractInteractor;
        this.knownRelaysManager = dependencies.knownRelaysManager;
        this.transactionValidator = dependencies.transactionValidator;
        this.accountManager = dependencies.accountManager;
        this.pingFilter = dependencies.pingFilter;
        this.asyncApprovalData = dependencies.asyncApprovalData;
        this.asyncPaymasterData = dependencies.asyncPaymasterData;
        loglevel_1.default.setLevel(this.config.logLevel);
    }
    /**
     * register a listener for GSN events
     * @see GsnEvent and its subclasses for emitted events
     * @param handler callback function to handle events
     */
    registerEventListener(handler) {
        this.emitter.on('gsn', handler);
    }
    /**
     * unregister previously registered event listener
     * @param handler callback function to unregister
     */
    unregisterEventListener(handler) {
        this.emitter.off('gsn', handler);
    }
    emit(event) {
        this.emitter.emit('gsn', event);
    }
    /**
     * In case Relay Server does not broadcast the signed transaction to the network,
     * client also broadcasts the same transaction. If the transaction fails with nonce
     * error, it indicates Relay may have signed multiple transactions with same nonce,
     * causing a DoS attack.
     *
     * @param {*} transaction - actual Ethereum transaction, signed by a relay
     */
    async _broadcastRawTx(transaction) {
        const rawTx = '0x' + transaction.serialize().toString('hex');
        const txHash = '0x' + transaction.hash(true).toString('hex');
        loglevel_1.default.info(`Broadcasting raw transaction signed by relay. TxHash: ${txHash}`);
        try {
            if (await this._isAlreadySubmitted(txHash)) {
                return { hasReceipt: true };
            }
            // can't find the TX in the mempool. broadcast it ourselves.
            await this.contractInteractor.sendSignedTransaction(rawTx);
            return { hasReceipt: true };
        }
        catch (broadcastError) {
            // don't display error for the known-good cases
            if ((broadcastError === null || broadcastError === void 0 ? void 0 : broadcastError.message.match(/the tx doesn't have the correct nonce|known transaction/)) != null) {
                return {
                    hasReceipt: false,
                    wrongNonce: true,
                    broadcastError
                };
            }
            return { hasReceipt: false, broadcastError };
        }
    }
    async _isAlreadySubmitted(txHash) {
        const [txMinedReceipt, pendingBlock] = await Promise.all([
            this.contractInteractor.web3.eth.getTransactionReceipt(txHash),
            // mempool transactions
            this.contractInteractor.web3.eth.getBlock('pending')
        ]);
        if (txMinedReceipt != null) {
            return true;
        }
        if (pendingBlock.transactions.includes(txHash)) {
            return true;
        }
        return false;
    }
    async _init() {
        if (this.initialized) {
            return;
        }
        this.emit(new GsnEvents_1.GsnInitEvent());
        await this.contractInteractor.init();
        this.initialized = true;
    }
    async relayTransaction(gsnTransactionDetails) {
        var _a, _b;
        await this._init();
        // TODO: should have a better strategy to decide how often to refresh known relays
        this.emit(new GsnEvents_1.GsnRefreshRelaysEvent());
        await this.knownRelaysManager.refresh();
        gsnTransactionDetails.gasPrice = (_a = gsnTransactionDetails.forceGasPrice) !== null && _a !== void 0 ? _a : await this._calculateGasPrice();
        if (gsnTransactionDetails.gas == null) {
            const estimated = await this.contractInteractor.estimateGas(gsnTransactionDetails);
            gsnTransactionDetails.gas = `0x${estimated.toString(16)}`;
        }
        const relaySelectionManager = await new RelaySelectionManager_1.default(gsnTransactionDetails, this.knownRelaysManager, this.httpClient, this.pingFilter, this.config).init();
        this.emit(new GsnEvents_1.GsnDoneRefreshRelaysEvent((relaySelectionManager.relaysLeft().length)));
        const relayingErrors = new Map();
        while (true) {
            let relayingAttempt;
            const activeRelay = await relaySelectionManager.selectNextRelay();
            if (activeRelay != null) {
                this.emit(new GsnEvents_1.GsnNextRelayEvent(activeRelay.relayInfo.relayUrl));
                relayingAttempt = await this._attemptRelay(activeRelay, gsnTransactionDetails)
                    .catch(error => ({ error }));
                if (relayingAttempt.transaction == null) {
                    relayingErrors.set(activeRelay.relayInfo.relayUrl, (_b = relayingAttempt.error) !== null && _b !== void 0 ? _b : new Error('No error reason was given'));
                    continue;
                }
            }
            return {
                transaction: relayingAttempt === null || relayingAttempt === void 0 ? void 0 : relayingAttempt.transaction,
                relayingErrors,
                pingErrors: relaySelectionManager.errors
            };
        }
    }
    async _calculateGasPrice() {
        const pct = this.config.gasPriceFactorPercent;
        const networkGasPrice = await this.contractInteractor.getGasPrice();
        let gasPrice = Math.round(parseInt(networkGasPrice) * (pct + 100) / 100);
        if (this.config.minGasPrice != null && gasPrice < this.config.minGasPrice) {
            gasPrice = this.config.minGasPrice;
        }
        return `0x${gasPrice.toString(16)}`;
    }
    async _attemptRelay(relayInfo, gsnTransactionDetails) {
        loglevel_1.default.info(`attempting relay: ${JSON.stringify(relayInfo)} transaction: ${JSON.stringify(gsnTransactionDetails)}`);
        const maxAcceptanceBudget = parseInt(relayInfo.pingResponse.maxAcceptanceBudget);
        const httpRequest = await this._prepareRelayHttpRequest(relayInfo, gsnTransactionDetails);
        this.emit(new GsnEvents_1.GsnValidateRequestEvent());
        const acceptRelayCallResult = await this.contractInteractor.validateAcceptRelayCall(maxAcceptanceBudget, httpRequest.relayRequest, httpRequest.metadata.signature, httpRequest.metadata.approvalData);
        if (!acceptRelayCallResult.paymasterAccepted) {
            let message;
            if (acceptRelayCallResult.reverted) {
                message = 'local view call to \'relayCall()\' reverted';
            }
            else {
                message = 'paymaster rejected in local view call to \'relayCall()\' ';
            }
            return { error: new Error(`${message}: ${Utils_1.decodeRevertReason(acceptRelayCallResult.returnValue)}`) };
        }
        let hexTransaction;
        this.emit(new GsnEvents_1.GsnSendToRelayerEvent(relayInfo.relayInfo.relayUrl));
        try {
            hexTransaction = await this.httpClient.relayTransaction(relayInfo.relayInfo.relayUrl, httpRequest);
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.message) == null || error.message.indexOf('timeout') !== -1) {
                this.knownRelaysManager.saveRelayFailure(new Date().getTime(), relayInfo.relayInfo.relayManager, relayInfo.relayInfo.relayUrl);
            }
            loglevel_1.default.info('relayTransaction: ', JSON.stringify(httpRequest));
            return { error };
        }
        const transaction = new ethereumjs_tx_1.Transaction(hexTransaction, this.contractInteractor.getRawTxOptions());
        if (!this.transactionValidator.validateRelayResponse(httpRequest, maxAcceptanceBudget, hexTransaction)) {
            this.emit(new GsnEvents_1.GsnRelayerResponseEvent(false));
            this.knownRelaysManager.saveRelayFailure(new Date().getTime(), relayInfo.relayInfo.relayManager, relayInfo.relayInfo.relayUrl);
            return { error: new Error('Returned transaction did not pass validation') };
        }
        this.emit(new GsnEvents_1.GsnRelayerResponseEvent(true));
        await this._broadcastRawTx(transaction);
        return {
            transaction
        };
    }
    async _prepareRelayHttpRequest(relayInfo, gsnTransactionDetails) {
        var _a, _b;
        const forwarderAddress = await this.resolveForwarder(gsnTransactionDetails);
        const paymaster = (_a = gsnTransactionDetails.paymaster) !== null && _a !== void 0 ? _a : this.config.paymasterAddress;
        const senderNonce = await this.contractInteractor.getSenderNonce(gsnTransactionDetails.from, forwarderAddress);
        const relayWorker = relayInfo.pingResponse.relayWorkerAddress;
        const gasPriceHex = gsnTransactionDetails.gasPrice;
        const gasLimitHex = gsnTransactionDetails.gas;
        if (gasPriceHex == null || gasLimitHex == null) {
            throw new Error('RelayClient internal exception. Gas price or gas limit still not calculated. Cannot happen.');
        }
        if (gasPriceHex.indexOf('0x') !== 0) {
            throw new Error(`Invalid gasPrice hex string: ${gasPriceHex}`);
        }
        if (gasLimitHex.indexOf('0x') !== 0) {
            throw new Error(`Invalid gasLimit hex string: ${gasLimitHex}`);
        }
        const gasLimit = parseInt(gasLimitHex, 16).toString();
        const gasPrice = parseInt(gasPriceHex, 16).toString();
        const value = (_b = gsnTransactionDetails.value) !== null && _b !== void 0 ? _b : '0';
        const relayRequest = {
            request: {
                to: gsnTransactionDetails.to,
                data: gsnTransactionDetails.data,
                from: gsnTransactionDetails.from,
                value: value,
                nonce: senderNonce,
                gas: gasLimit
            },
            relayData: {
                pctRelayFee: relayInfo.relayInfo.pctRelayFee,
                baseRelayFee: relayInfo.relayInfo.baseRelayFee,
                gasPrice,
                paymaster,
                paymasterData: '',
                clientId: this.config.clientId,
                forwarder: forwarderAddress,
                relayWorker
            }
        };
        // put paymasterData into struct before signing
        relayRequest.relayData.paymasterData = await this.asyncPaymasterData(relayRequest);
        this.emit(new GsnEvents_1.GsnSignRequestEvent());
        const signature = await this.accountManager.sign(relayRequest);
        const approvalData = await this.asyncApprovalData(relayRequest);
        // max nonce is not signed, as contracts cannot access addresses' nonces.
        const transactionCount = await this.contractInteractor.getTransactionCount(relayWorker);
        const relayMaxNonce = transactionCount + this.config.maxRelayNonceGap;
        // TODO: the server accepts a flat object, and that is why this code looks like shit.
        //  Must teach server to accept correct types
        const metadata = {
            relayHubAddress: this.config.relayHubAddress,
            signature,
            approvalData,
            relayMaxNonce
        };
        const httpRequest = {
            relayRequest,
            metadata
        };
        loglevel_1.default.info(`Created HTTP relay request: ${JSON.stringify(httpRequest)}`);
        return httpRequest;
    }
    async resolveForwarder(gsnTransactionDetails) {
        var _a;
        let forwarderAddress = (_a = gsnTransactionDetails.forwarder) !== null && _a !== void 0 ? _a : this.config.forwarderAddress;
        if (forwarderAddress !== Constants_1.constants.ZERO_ADDRESS) {
            const isRecipientDeployed = await this.contractInteractor.isContractDeployed(gsnTransactionDetails.to);
            if (!isRecipientDeployed) {
                console.warn(`No IRelayRecipient code at ${gsnTransactionDetails.to}, proceeding without validating 'isTrustedForwarder'!
        Unless you are using some counterfactual contract deployment technique the transaction will fail!`);
            }
            else {
                const isTrusted = await this.contractInteractor.isTrustedForwarder(gsnTransactionDetails.to, forwarderAddress);
                if (!isTrusted) {
                    throw new Error('The Forwarder address configured but is not trusted by the Recipient contract');
                }
            }
        }
        else {
            try {
                loglevel_1.default.info(`will attempt to get trusted forwarder from: ${gsnTransactionDetails.to}`);
                forwarderAddress = await this.contractInteractor.getForwarder(gsnTransactionDetails.to);
                loglevel_1.default.info(`on-chain forwarder for: ${gsnTransactionDetails.to} is ${forwarderAddress}`);
            }
            catch (e) {
                throw new Error('No forwarder address configured and no getTrustedForwarder in target contract (fetching from Recipient failed)');
            }
        }
        return forwarderAddress;
    }
}
exports.RelayClient = RelayClient;
function _dumpRelayingResult(relayingResult) {
    let str = '';
    if (relayingResult.pingErrors.size > 0) {
        str += `Ping errors (${relayingResult.pingErrors.size}):`;
        Array.from(relayingResult.pingErrors.keys()).forEach(e => {
            var _a, _b;
            const err = relayingResult.pingErrors.get(e);
            const error = (_b = (_a = err === null || err === void 0 ? void 0 : err.message) !== null && _a !== void 0 ? _a : err === null || err === void 0 ? void 0 : err.toString()) !== null && _b !== void 0 ? _b : '';
            str += `\n${e} => ${error}\n`;
        });
    }
    if (relayingResult.relayingErrors.size > 0) {
        str += `Relaying errors (${relayingResult.relayingErrors.size}):\n`;
        Array.from(relayingResult.relayingErrors.keys()).forEach(e => {
            var _a, _b;
            const err = relayingResult.relayingErrors.get(e);
            const error = (_b = (_a = err === null || err === void 0 ? void 0 : err.message) !== null && _a !== void 0 ? _a : err === null || err === void 0 ? void 0 : err.toString()) !== null && _b !== void 0 ? _b : '';
            str += `${e} => ${error}`;
        });
    }
    return str;
}
exports._dumpRelayingResult = _dumpRelayingResult;
//# sourceMappingURL=RelayClient.js.map